import React, { useMemo } from 'react';
import {
  BankDepositIcon,
  BillingCheckIcon,
  Box,
  FeeLabel,
  PayPalIcon,
  QuestionIcon,
  Text,
  VenmoIcon,
} from '@common-fe/common-fe';
import styled from 'styled-components';

import { PaymentMethod, PaymentMethodType } from '@/modules/employee/employee.types';
import spacings from '@/styles/spacings';
import theme from '@/styles/theme';
import { getFullAddress } from '@/utils';

const StyledBox = styled(Box)`
  ${({ theme }) => theme.border.blueGray2}
  border-radius: ${({ theme }) => theme.rounds.container2Round} ;
`;

interface Props {
  paymentMethod?: Partial<PaymentMethod>;
  simplifyDesc?: boolean;
  paymentEmployeeFee?: string;
}

export const PaymentMethodLabel: React.FC<Props> = ({
  paymentMethod,
  simplifyDesc,
  paymentEmployeeFee,
}) => {
  const currentIcon = useMemo(() => {
    const { type } = paymentMethod || {};
    switch (type) {
    case PaymentMethodType.PAYPAL:
      return <PayPalIcon data-testid={`PaymentMethodLabel_icon_${type}`} />;
    case PaymentMethodType.VENMO:
      return <VenmoIcon data-testid={`PaymentMethodLabel_icon_${type}`} />;
    case PaymentMethodType.CHECK:
      return (
        <BillingCheckIcon
          data-testid={`PaymentMethodLabel_icon_${type}`}
          size={spacings.l}
          color="iconAccent"
        />
      );
    case PaymentMethodType.DEBIT:
      return (
        <BillingCheckIcon
          data-testid={`PaymentMethodLabel_icon_${type}`}
          size={spacings.l}
          color="iconAccent"
        />
      );
    case PaymentMethodType.DIRECT_DEPOSIT:
      return (
        <BankDepositIcon
          data-testid={`PaymentMethodLabel_icon_${type}`}
          size={spacings.l}
          color="iconAccent"
        />
      );
    default:
      return (
        <QuestionIcon
          data-testid="PaymentMethodLabel_no_icon"
          size={spacings.l}
          color="iconAccent"
        />
      );
    }
  }, [paymentMethod]);

  const fee = useMemo(() => {
    if (!paymentMethod?.type) return null;

    return (
      <FeeLabel
        amount={paymentEmployeeFee}
        testId={paymentMethod?.type}
        style={{ marginTop: theme.spacings.spacing2 }}
      />
    );
  }, [paymentMethod, paymentEmployeeFee]);

  const currentContent = useMemo(() => {
    const {
      type,
      // accountNumber,
      accountDescription,
      isVerified,
      accountNumberLast4,
    } = paymentMethod || {};

    if (!paymentMethod) {
      return (
        <Box data-testid={`PaymentMethodLabel_content_${type}`}>
          <Text size={spacings.m} color="textTitle">
            No payment method added
          </Text>
        </Box>
      );
    }
    switch (type) {
    case PaymentMethodType.PAYPAL:
      return (
        <Box data-testid={`PaymentMethodLabel_content_${type}`}>
          {paymentMethod.requisites?.email && (
            <Text size={spacings.m} color="textTitle">
              {paymentMethod.requisites?.email}
            </Text>
          )}
          {paymentMethod.requisites?.phoneNumber && (
            <Text
              size={paymentMethod.requisites?.email ? spacings.s : spacings.m}
              color={paymentMethod.requisites?.email ? 'textTitle' : 'textSecondary'}
              weight={500}
            >
              {paymentMethod.requisites?.phoneNumber}
            </Text>
          )}
          {fee}
        </Box>
      );
    case PaymentMethodType.VENMO:
      return paymentMethod.requisites?.phoneNumber && (
        <Box data-testid={`PaymentMethodLabel_content_${type}`}>
          <Text size={spacings.m} color="textTitle">
            {paymentMethod.requisites?.phoneNumber}
          </Text>
          {fee}
        </Box>
      );
    case PaymentMethodType.DEBIT:
      return (
        <Box data-testid={`PaymentMethodLabel_content_${type}`}>
          <Text size={spacings.m} color="textTitle">
            {paymentMethod.requisites?.cardType} (•••• {paymentMethod.requisites?.cardLast4})
          </Text>
          {fee}
        </Box>
      );
    case PaymentMethodType.DIRECT_DEPOSIT:
      return (
        <Box data-testid={`PaymentMethodLabel_content_${type}`}>
          <Text size={spacings.m} color="textTitle">
            {`${accountDescription} ${simplifyDesc ? '(ends ****)' : `(Bank a/c number ends ${accountNumberLast4 || '****'})`}`}
          </Text>
          {isVerified && (
            <Box direction="row" gap={spacings.xxxs}>
              <Text size={spacings.s} color="textSecondary" weight={500}>
                Verified on
              </Text>
              <Text size={spacings.s} color="success" weight={500}>
                06/28/21
              </Text>
            </Box>
          )}
          {fee}
        </Box>
      );
    case PaymentMethodType.CHECK:
      return (
        <Box data-testid={`PaymentMethodLabel_content_${type}`}>
          <Text size={spacings.m} color="textTitle">
            {getFullAddress(paymentMethod?.address)}
          </Text>
          {fee}
        </Box>
      );

    default:
      return null;
    }
  }, [paymentMethod, simplifyDesc, fee]);

  return (
    <Box
      data-testid="PaymentMethodLabel_container"
      direction="row"
      align="center"
    >
      <StyledBox
        pad={spacings.xxs}
        margin={{ right: spacings.s }}
        height={{ min: '40px', max: '40px' }}
        width={{ min: '40px', max: '40px' }}
        align="center"
        justify="center"
        data-testid="PaymentMethodLabel_icon"
      >
        {currentIcon}
      </StyledBox>

      {currentContent}

    </Box>

  );
};
